import {
  Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, ChangeDetectorRef, Input
} from '@angular/core';
import { Subscription } from 'rxjs';
import { scheduleOrphanedOverlayMaskSweep } from 'src/app/commons/utils/overlay-mask-sweep';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Drawer } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { Popover } from 'primeng/popover';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { BadgeModule } from 'primeng/badge';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpErrorResponse } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { APP_VERSION } from 'src/environments/version';

import {
  AiChatService, AiChatStatus, AutoRequest, AutoResponse, ChatMessage, ChatResponse, ChatSession
} from '../../../services/ai-chat/ai-chat.service';
import {
  AiDataAssistantService, DataAssistantAskResponse, DataAssistantCandidate, DataAssistantMessage, DataAssistantSession
} from '../../../services/ai-chat/ai-data-assistant.service';
import { FEEDBACK_REASONS } from '../../../services/ai-chat/feedback-reason.constants';
import { PermissionService } from '../../../services/permission.service';
import { PlanLimitService, PlanUsageItemDTO } from '../../../services/plan-limit/plan-limit.service';
import { SideBarMenuService } from '../../../services/side-bar-menu.service';
import { AiChatWidgetSharedService } from '../../../services/ai-chat-widget-shared.service';
import { NetworkStatusService } from '../../../services/network-status.service';
import { isOfflineCapableNative } from '../../../commons/utils/platform';
import { TranslatePipe } from '../../../i18n/translate.pipe';
import { TranslationService } from '../../../i18n/translation.service';
import { MediaCaptureButtonComponent } from '../../../commons/components/media-capture-button/media-capture-button.component';
import { formatInProfileZone } from 'src/app/commons/pipes/user-date.pipe';

/** 'auto' = the single Linc box (the server picks Help or Data for each question); 'kb' = Help only;
 *  'data' = Linc Data only. The two single-engine modes behave exactly as before. */
export type AiWidgetMode = 'auto' | 'kb' | 'data';

/** One row of the merged thread shown in Auto mode. Each row points at the SAME message object that
 *  was also added to the Help thread (`messages`) or the Data thread (`dataMessages`), so switching
 *  to the Help or Assistant tab still shows the matching half of the conversation. */
export interface AutoThreadItem {
  key: number;
  engine: 'kb' | 'data';
  kb?: ChatMessage & { sources?: string[] };
  data?: DataChatMessage;
  sources: string[];
  /** Set on the how-to half of a mixed question when the data half could not be answered. */
  note?: string | null;
}

/** Rich-answer formatting, added 2026-08-23 — ONE method (getFormattedAnswer, see below) decides
 *  which of these shapes an assistant answer gets, computed once when the message is built and
 *  stored here so the template never has to recompute it. 'text' reuses the exact same
 *  formatMessage() escape+bold/italic+linebreak rendering already used everywhere — nothing about
 *  that safe path changes; the other 3 shapes are purely additive presentations of the SAME
 *  content/data, never a rewording of it. */
export type BadgeSeverity = 'success' | 'info' | 'warn' | 'danger';

export interface FormattedTableColumn { key: string; label: string; }

export interface RankedItem {
  rank: number;
  companyName: string;
  onTimeLabel: string | null;
  onTimeSeverity: BadgeSeverity | null;
  lossLabel: string | null;
  lossSeverity: BadgeSeverity | null;
  outturnLabel: string | null;
  shipmentCount: number;
}

export type FormattedAnswer =
  | { kind: 'text'; html: SafeHtml }
  | { kind: 'table'; columns: FormattedTableColumn[]; rows: Record<string, string>[] }
  | { kind: 'ranked'; items: RankedItem[] }
  | { kind: 'stat'; items: { label: string; value: string }[] };

/** A "Linc Data" turn, shaped to render through the SAME message-bubble markup the KB thread
 *  already uses — see the template's ai-msg-wrapper block, now shared by both modes.
 *  deepLink/candidates are always present (never `undefined`, defaulted to null/[] wherever this
 *  is built) so the template can bind *ngFor/*ngIf against them directly without narrowing an
 *  optional field. */
export interface DataChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  deepLink: string | null;
  resolved: boolean;
  candidates: DataAssistantCandidate[];
  /** Added 2026-08-22 — the real backend AiDataAssistantQueryLog row id for this turn, used for
   *  feedback. Distinct from `id` above (a local, client-side sequence number only used for
   *  *ngFor tracking) — never reuse `id` for feedback, it isn't a real backend id. Null for the
   *  user's own messages and for a turn that resolved for free (still gets its own log row, see
   *  DataAssistantResponseDto's own note) only if the ask() call itself errored before logging. */
  queryLogId?: number | null;
  /** Added 2026-08-22 (Phase 2d) — the real credit cost of this turn. Null (not 0) means no real
   *  AI call happened this turn (free disambiguation-resume, greeting, or block message). */
  creditsUsed?: number | null;
  /** Added 2026-08-23 — the same structured data/intent the ask() response already carried, kept
   *  here (only for a message built in THIS live session — never present after a History reload,
   *  since the backend doesn't persist this) purely so getFormattedAnswer() can build a table/
   *  ranked-list/stat presentation from it. Not read anywhere else. */
  data?: Record<string, unknown> | null;
  intent?: string | null;
  /** Always present — computed once when the message is built, see getFormattedAnswer(). */
  formatted: FormattedAnswer;
  /** Added 2026-08-23 — "you might also ask" suggestions, only ever present alongside a genuine,
   *  fully resolved answer with no `candidates` pending (see the backend's own note on why the two
   *  are kept separate). Always an array (never undefined) so the template never needs an extra
   *  null-guard — defaults to `[]` everywhere a message is built without a real ask() response. */
  relatedQuestions: string[];
  /** Added 2026-08-23 — true only while animateCreditsCountdown() is actively ticking this
   *  message's `creditsUsed` down after a feedback reward, so the template can apply a brief
   *  scale/glow pulse and actually draw the eye to the number changing (the countdown itself was
   *  real but easy to miss at this small a font size). Optional — only ever set on a message this
   *  countdown has actually touched. */
  creditsAnimating?: boolean;
  /** Added 2026-08-23 — the reward amount, set for the same brief window as creditsAnimating, so
   *  the template can pop up a small floating "+N credits back" badge above the number instead of
   *  relying on the number's own pulse alone to be noticed. Optional, cleared the same way. */
  creditsRewardBadgeAmount?: number;
}

@Component({
    selector: 'app-ai-chat-widget',
    imports: [
        CommonModule,
        FormsModule,
        Drawer,
        ButtonModule,
        Popover,
        DialogModule,
        InputTextModule,
        TooltipModule,
        TableModule,
        TagModule,
        BadgeModule,
        TranslatePipe,
        MediaCaptureButtonComponent,
    ],
    templateUrl: './ai-chat-widget.component.html',
    styleUrls: ['./ai-chat-widget.component.css']
})
export class AiChatWidgetComponent implements OnInit, AfterViewChecked, OnDestroy {

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('inputArea') private inputArea!: ElementRef;

  @Input() hideFab = false;

  chatEnabled = false;
  chatOpen = false;
  loading = false;
  fabPulse = false;
  historyOpen = false;

  /** Minimum time the "thinking" indicator stays visible — see finishAfterMinLoading()'s note. */
  private static readonly MIN_LOADING_MS = 500;
  private loadingStartedAt = 0;

  status: AiChatStatus = {
    enabled: false,
    blocked: false,
    blockedMessage: null,
    blockedRemainingSeconds: 0,
    dailyCount: 0,
    dailyLimit: 25
  };

  messages: (ChatMessage & { sources?: string[] })[] = [];
  sessions: ChatSession[] = [];
  currentSessionId: number | null = null;
  msgSources: string[][] = [];
  feedbackMap: Record<number, 1 | -1> = {};
  /** Added 2026-08-22 — Linc Data's own feedback map, kept separate from feedbackMap above rather
   *  than sharing it: KB message ids and Linc Data queryLogIds come from two different backend
   *  tables/id-spaces, so a shared map risks one mode showing a stale "active" thumb keyed by a
   *  coincidentally-matching id from the other mode. Keyed by the real queryLogId, never the
   *  local `id` sequence. */
  dataFeedbackMap: Record<number, 1 | -1> = {};

  /** Added 2026-08-23 — the "why" reason-chip row shown after a thumbs-down, see
   *  submitFeedback()/submitDataFeedback()'s own note. `activeXReasonPromptId` is which message
   *  currently shows its chip row (null = none); `xReasonMap` tracks the picked reason per message
   *  id for highlighting, same optimistic-then-rollback shape as feedbackMap/dataFeedbackMap
   *  above. Kept as two separate pairs (KB vs Data) for the exact same id-space reason
   *  dataFeedbackMap's own note gives. */
  readonly feedbackReasons = FEEDBACK_REASONS;
  activeReasonPromptId: number | null = null;
  activeDataReasonPromptId: number | null = null;
  reasonMap: Record<number, string> = {};
  dataReasonMap: Record<number, string> = {};

  inputText = '';

  /** Added 2026-08-23 — real profile photo (or real initial) for the "you" avatar in this widget's
   *  own chat bubbles, replacing a permanently-hardcoded 'U'. Same source/pattern HeaderComponent
   *  already uses for its own avatar (`SideBarMenuService.getDecryptedUserProfile()` +
   *  `avatarUpdated$` for live updates after a photo change) — reused as-is rather than inventing a
   *  second way to read "the current user's own profile picture". */
  private userProfile: any = null;
  private offlineAvatarUrl: string | null = null;
  private avatarSub?: Subscription;
  private openWithQuestionSub?: Subscription;
  private openRequestedSub?: Subscription;

  get userInitial(): string {
    return this.userProfile?.firstName?.charAt(0)?.toUpperCase() || 'U';
  }

  /** Same online/offline fallback shape as HeaderComponent.getAvatarImgSrc() — online (or not a
   *  native offline-capable build), the live signed URL/OAuth avatar renders directly; offline,
   *  falls back to whatever was already cached, else undefined so the template falls through to
   *  the initials circle instead of a broken-image icon. */
  getAvatarImgSrc(): string | undefined {
    if (this.networkStatusService.isOnline || !isOfflineCapableNative()) {
      return this.userProfile?.profilePicture;
    }
    return this.offlineAvatarUrl ?? undefined;
  }

  pendingAttachment: { name: string; type: string; base64: string } | null = null;

  soundEnabled = localStorage.getItem('ai-chat-sound') !== 'false';
  private audioCtx: AudioContext | null = null;

  // ── Resizable drawer width — added 2026-08-23 ──────────────────────────
  private static readonly DRAWER_MIN_WIDTH = 360;
  private static readonly DRAWER_MAX_WIDTH = 720;
  private static readonly DRAWER_WIDTH_KEY = 'ai-chat-drawer-width';
  /** Same default as the drawer's own previous fixed value — nobody sees any change unless they
   *  actually drag the new handle. */
  drawerWidthPx = (() => {
    const saved = Number(localStorage.getItem(AiChatWidgetComponent.DRAWER_WIDTH_KEY));
    return saved >= AiChatWidgetComponent.DRAWER_MIN_WIDTH && saved <= AiChatWidgetComponent.DRAWER_MAX_WIDTH
      ? saved : 430;
  })();
  isResizingDrawer = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  /** The real p-drawer panel DOM node, grabbed once per drag — see onResizeMove's own note on
   *  why this is written to directly instead of relying on the [style] binding during a drag. */
  private resizePanelEl: HTMLElement | null = null;

  emojis = ['🌲','🪵','📦','🚢','✅','❌','📋','🔍','💡','🎯',
             '📊','⚙️','🔔','📤','📥','🗂️','🏭','🌿','⚓','🔒'];

  showEmailDialog = false;
  transcriptEmail = '';

  private scrollToBottomNeeded = false;

  // ── "Linc Data" mode (AI Data Assistant) ─────────────────────────────────
  // Two fully independent conversation threads, one per engine — switching mode swaps which
  // thread is visible rather than mixing both engines' answers into one list (see the mode
  // switcher's own design note: "mandatorily use whichever engine is selected").
  mode: AiWidgetMode = 'kb';

  // ── Auto mode (the single Linc box) ───────────────────────────────────────
  // Offered only when the server says so (switch on AND Linc Data usable for this person). The
  // merged thread below is a view over the Help and Data threads, not a third conversation store.
  autoAvailable = false;
  autoLoading = false;
  autoThread: AutoThreadItem[] = [];
  private autoSeq = 0;
  /** Which engine answered the last Auto turn, so a short follow-up stays with it. */
  private lastEngine: 'help' | 'data' | null = null;
  /** Set after a Data question that asked "did you mean ...?", so the tapped/typed reply goes back to Data. */
  private pendingForce: 'data' | null = null;
  /** True once the person has picked a mode by hand: from then on the server never changes it for them. */
  private modeChosenByUser = false;
  private static readonly MODE_KEY = 'ai-chat-mode';

  // The question each chip sends is a translation key too, so a person using the app in Hindi, Portuguese,
  // French or Spanish sends the question in that language and Linc answers in it.
  autoStarters = [
    { icon: 'pi pi-file-plus',  labelKey: 'ai.chat.starter.createConsignment', qKey: 'ai.chat.starterQ.createConsignment' },
    { icon: 'pi pi-user-plus',  labelKey: 'ai.chat.starter.inviteTeam',        qKey: 'ai.chat.starterQ.inviteTeam' },
    { icon: 'pi pi-share-alt',  labelKey: 'ai.chat.starter.shareTally',        qKey: 'ai.chat.starterQ.shareTally' },
    { icon: 'pi pi-box',        labelKey: 'ai.chat.starter.stockNow',          qKey: 'ai.chat.starterQ.stockNow' },
    { icon: 'pi pi-wallet',     labelKey: 'ai.chat.starter.toPay',             qKey: 'ai.chat.starterQ.toPay' },
    { icon: 'pi pi-shield',     labelKey: 'ai.chat.starter.myAccess',          qKey: 'ai.chat.starterQ.myAccess' },
  ];

  /** The chip's question in the app's current language. */
  starterQuestion(qKey: string): string {
    return this.translation.instant(qKey);
  }

  // Layer 1 (plan/tier) — layer 2 (RBAC) reads live off PermissionService's own BehaviorSubject
  // via canUseDataMode() below, not cached here, so a freshly-granted permission is picked up
  // without needing its own separate refresh call.
  private dataAssistantTierEnabled = false;

  dataLoading = false;
  dataSessionId: string | null = null;
  dataMessages: DataChatMessage[] = [];
  dataSessions: DataAssistantSession[] = [];
  private dataMsgSeq = 0;

  dataStarters = [
    { icon: 'pi pi-box',        qKey: 'ai.chat.starterQ.stockNow' },
    { icon: 'pi pi-truck',      qKey: 'ai.chat.starterQ.shipmentsMonth' },
    { icon: 'pi pi-wallet',     qKey: 'ai.chat.starterQ.toPay' },
    { icon: 'pi pi-chart-line', qKey: 'ai.chat.starterQ.profitMargin' },
    { icon: 'pi pi-cog',        qKey: 'ai.chat.starterQ.machines' },
    { icon: 'pi pi-star',       qKey: 'ai.chat.starterQ.health' },
  ];

  // ── Combined AI disclaimer (Linc + "Linc Data") ───────────────────────────
  // null = not checked yet (nothing renders while null, so the gate screen never flashes on
  // top of a user who already accepted). Checked once per widget open, not just once at page
  // load, so a version bump takes effect the same "instant" way every other gate in this widget
  // already does.
  disclaimerAccepted: boolean | null = null;
  disclaimerChecked = false;
  disclaimerAccepting = false;

  /** Added 2026-08-22 — the unified "AI Credits" usage item (Phase 4: every AI feature now
   *  shares one credit pool/number, KB chat included). Null until loaded, or if the request
   *  fails, or if the tenant has no AI feature enabled at all (the backend simply omits the item
   *  in that case — see PlanEnforcementServiceImpl's own note). Never shown with the "at limit"
   *  alarm styling used elsewhere in the app for this same key — see the template's own note on
   *  why. */
  aiCreditsUsage: PlanUsageItemDTO | null = null;

  constructor(
    private aiChatService: AiChatService,
    private aiDataAssistantService: AiDataAssistantService,
    public permissionService: PermissionService,
    private planLimitService: PlanLimitService,
    private sideBarMenuService: SideBarMenuService,
    private aiChatWidgetSharedService: AiChatWidgetSharedService,
    private networkStatusService: NetworkStatusService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private translation: TranslationService
  ) {
    // Quick Search's "Ask Linc" card (and any other future caller) — opens the drawer with the
    // question pre-filled but NOT auto-sent, so the user reviews/edits before it spends a quota
    // message. See AiChatWidgetSharedService's own doc comment.
    // 2026-09-15: opens on the Assistant (data) tab — a Quick Search term like "Costa Rica" or
    // "4x3x6" is a question about the company's own records/stock, not about how to use the app.
    // Users without Assistant access (tier off or no DATA_ASSISTANT_ACCESS) still get Help.
    this.openWithQuestionSub = this.aiChatWidgetSharedService.openWithQuestion.subscribe((question: string) => {
      // With the single Auto box on, the question goes there (the server picks Help or Data);
      // otherwise exactly as before.
      this.setMode(this.autoAvailable ? 'auto' : (this.canUseDataMode ? 'data' : 'kb'));
      if (!this.chatOpen) {
        this.chatOpen = true;
        this.openDrawer();
      }
      this.inputText = question;
    });
    // "Ask Linc" in the profile menu: just open the chat (same as the round button did).
    this.openRequestedSub = this.aiChatWidgetSharedService.openRequested.subscribe(() => {
      if (!this.chatOpen) {
        this.chatOpen = true;
        this.fabPulse = false;
        this.openDrawer();
      }
    });
  }

  ngOnInit(): void {
    this.refreshStatus();
    this.refreshDataAssistantGate();
    this.refreshAutoStatus();
    this.refreshDisclaimerStatus();
    this.refreshAiCreditsUsage();
    this.loadUserAvatar();
  }

  /** Asks the server whether to offer the Auto box. If yes and the person has not chosen a mode by
   *  hand, Auto becomes the starting mode (or the one they used last time). If the server says no
   *  (switched off, or Linc Data no longer usable), an Auto screen falls back to Help. */
  private refreshAutoStatus(): void {
    this.aiChatService.autoStatus().subscribe({
      next: (res) => {
        const wasAvailable = this.autoAvailable;
        this.autoAvailable = !!res.available;
        if (this.autoAvailable && !wasAvailable && !this.modeChosenByUser) {
          this.mode = this.readStoredMode() ?? 'auto';
          if (this.mode === 'data' && !this.canUseDataMode) this.mode = 'auto';
        }
        this.enforceAutoAccess();
        this.cdr.detectChanges();
      },
      error: () => { this.autoAvailable = false; this.enforceAutoAccess(); },
    });
  }

  private enforceAutoAccess(): void {
    if (this.mode !== 'auto' || this.autoAvailable) return;
    this.mode = 'kb';
    this.historyOpen = false;
    this.scrollToBottomNeeded = true;
  }

  /** The last mode the person picked here, remembered per browser. Storage can be blocked (private
   *  window, cleared site data), so every read and write is wrapped. */
  private readStoredMode(): AiWidgetMode | null {
    try {
      const v = localStorage.getItem(AiChatWidgetComponent.MODE_KEY);
      return v === 'auto' || v === 'kb' || v === 'data' ? v : null;
    } catch {
      return null;
    }
  }

  private storeMode(mode: AiWidgetMode): void {
    try {
      localStorage.setItem(AiChatWidgetComponent.MODE_KEY, mode);
    } catch {
      // nothing to do: the mode just is not remembered
    }
  }

  ngOnDestroy(): void {
    this.avatarSub?.unsubscribe();
    this.openWithQuestionSub?.unsubscribe();
    this.openRequestedSub?.unsubscribe();
    if (this.offlineAvatarUrl) {
      URL.revokeObjectURL(this.offlineAvatarUrl);
    }
  }

  /** Same cache-then-live-patch shape as HeaderComponent.ngAfterViewInit() — load once from the
   *  already-decrypted profile cache, then stay in sync via avatarUpdated$ if the photo changes
   *  while this widget is open. */
  private loadUserAvatar(): void {
    this.sideBarMenuService.getDecryptedUserProfile().then((profile) => {
      this.userProfile = profile;
      this.cdr.detectChanges();
    });
    this.sideBarMenuService.getCachedAvatarUrl().then((url) => {
      this.offlineAvatarUrl = url;
    });
    this.avatarSub = this.sideBarMenuService.avatarUpdated$.subscribe((profilePicture) => {
      if (this.userProfile) {
        this.userProfile = { ...this.userProfile, profilePicture };
      }
    });
  }

  /** Added 2026-08-22. Read-only display, fully separate from refreshStatus()/AiChatStatus —
   *  deliberately NOT merged into that call, since the daily-count/off-topic-block UI depends on
   *  that exact existing response shape and must stay untouched (see Phase 4 plan). Cached (30s
   *  TTL, shared with other pages via PlanLimitService) so opening the widget doesn't add its own
   *  extra network round trip on top of whatever else already called it this page load. */
  private refreshAiCreditsUsage(): void {
    this.planLimitService.getUsageCached().subscribe({
      next: (res) => {
        this.aiCreditsUsage = res.features.find(f => f.featureKey === 'ai_import') ?? null;
        this.cdr.detectChanges();
      },
      error: () => { this.aiCreditsUsage = null; },
    });
  }

  private refreshDisclaimerStatus(): void {
    this.aiChatService.checkDisclaimerStatus().subscribe({
      next: (res) => { this.disclaimerAccepted = res.accepted; },
      // Fail CLOSED here, not open — an error checking consent status must never silently let
      // someone send a message as if they'd already agreed to the disclaimer.
      error: () => { this.disclaimerAccepted = false; },
    });
  }

  acceptDisclaimer(): void {
    if (!this.disclaimerChecked || this.disclaimerAccepting) return;
    this.disclaimerAccepting = true;
    this.aiChatService.acceptDisclaimer().subscribe({
      next: () => {
        this.disclaimerAccepting = false;
        this.disclaimerAccepted = true;
      },
      error: () => { this.disclaimerAccepting = false; },
    });
  }

  /** Both _doSend() and _doSendData() call this on a 403 whose body is exactly
   *  "DISCLAIMER_NOT_ACCEPTED" — a defensive backstop for the rare case where this tab's local
   *  state drifted from the server's (e.g. consent was somehow never recorded). Re-shows the gate
   *  instead of surfacing a raw, confusing error bubble. */
  private isDisclaimerError(err: HttpErrorResponse): boolean {
    return err.status === 403 && (err.error?.message === 'DISCLAIMER_NOT_ACCEPTED' || err.error === 'DISCLAIMER_NOT_ACCEPTED');
  }

  /** Layer 1 (plan/tier). Called on init AND every time the drawer is opened (see toggleChat())
   *  — a ROOT-granted addon or a plan upgrade must not require a full page reload to take effect,
   *  same "instant" bar as layer 2's permission check already clears via PermissionService's own
   *  live navigation-driven refresh. */
  private refreshDataAssistantGate(): void {
    this.aiDataAssistantService.checkTenantStatus().subscribe({
      next: (res) => { this.dataAssistantTierEnabled = res.dataAssistantEnabled; this.enforceDataModeAccess(); },
      error: () => { this.dataAssistantTierEnabled = false; this.enforceDataModeAccess(); },
    });
  }

  /** 2026-09-16 — hidden = no access. If the plan/tier or DATA_ASSISTANT_ACCESS was taken away while
   *  this widget sat on the Assistant (data) tab, drop back to Help and clear what that tab showed,
   *  instead of leaving old answers on screen and reloading history the server now refuses. */
  private enforceDataModeAccess(): void {
    if (this.mode !== 'data' || this.canUseDataMode) return;
    this.mode = 'kb';
    this.historyOpen = false;
    this.dataSessions = [];
    this.dataSessionId = null;
    this.scrollToBottomNeeded = true;
  }

  /** Both layers must pass — plan/tier (layer 1, above) AND RBAC (layer 2, live off
   *  PermissionService). Admin-only by default; a tenant ADMIN can grant DATA_ASSISTANT_ACCESS to
   *  anyone via Manage Roles. */
  get canUseDataMode(): boolean {
    return this.dataAssistantTierEnabled && this.permissionService.hasEffective('DATA_ASSISTANT_ACCESS');
  }

  /** The mode switch shows when there is more than the Help tab to choose from. */
  get showModeSwitch(): boolean {
    return this.autoAvailable || this.canUseDataMode;
  }

  /** Tabs in the switch, left to right: Auto (if offered), Help, Assistant (if allowed). */
  private get modeTabs(): AiWidgetMode[] {
    const tabs: AiWidgetMode[] = [];
    if (this.autoAvailable) tabs.push('auto');
    tabs.push('kb');
    if (this.canUseDataMode) tabs.push('data');
    return tabs;
  }

  get modeTabCount(): number {
    return this.modeTabs.length;
  }

  /** Position of the current mode among the visible tabs — drives the sliding highlight. */
  get modeTabIndex(): number {
    return Math.max(0, this.modeTabs.indexOf(this.mode));
  }

  setMode(mode: AiWidgetMode): void {
    if (mode === this.mode) return;
    if (mode === 'data' && !this.canUseDataMode) return;
    if (mode === 'auto' && !this.autoAvailable) return;
    this.mode = mode;
    this.modeChosenByUser = true;
    this.storeMode(mode);
    this.historyOpen = false;
    this.scrollToBottomNeeded = true;
    if (mode === 'data') this.loadDataSessions();
  }

  ngAfterViewChecked(): void {
    if (this.scrollToBottomNeeded) {
      this.scrollToBottom();
      this.scrollToBottomNeeded = false;
    }
  }

  private refreshStatus(): void {
    this.aiChatService.checkStatus().subscribe({
      next: (res) => {
        this.status = res;
        this.chatEnabled = res.enabled;
        if (this.chatEnabled) {
          // Real bug, found live 2026-08-21: this used to also require !res.blocked, so a user
          // temporarily rate-limited from sending NEW messages lost the History button entirely —
          // sessions never loaded, so it silently showed nothing when clicked. Reading past history
          // has nothing to do with the send-side block (confirmed: the /chat/sessions endpoint has
          // no block check of its own) — only the welcome-nudge pulse below stays gated on !blocked,
          // since that one's just a cosmetic invite to start a NEW chat.
          this.loadSessions();
          if (!res.blocked) {
            setTimeout(() => { this.fabPulse = true; }, 800);
          }
        }
      },
      error: () => { this.chatEnabled = false; }
    });
  }

  /** Whichever engine is currently active — used by the shared header/status markup so it
   *  doesn't need a mode ternary at every single binding. */
  get isBusy(): boolean {
    if (this.mode === 'auto') return this.autoLoading;
    return this.mode === 'data' ? this.dataLoading : this.loading;
  }

  get dailyLimitExhausted(): boolean {
    return this.status.dailyLimit > 0 && this.status.dailyCount >= this.status.dailyLimit;
  }

  get dailyLimitPercent(): number {
    if (!this.status.dailyLimit || this.status.dailyLimit < 0) return 0;
    return Math.min(100, Math.round((this.status.dailyCount / this.status.dailyLimit) * 100));
  }

  get dailyProgressClass(): string {
    const p = this.dailyLimitPercent;
    if (p >= 100) return 'limit-full';
    if (p >= 80) return 'limit-high';
    if (p >= 50) return 'limit-mid';
    return 'limit-ok';
  }

  // Real gap found 2026-07-27: the main sidebar drawer already self-heals from an orphaned
  // overlay mask (interrupted close animation leaving a click-swallowing div behind — see
  // overlay-mask-sweep.ts) via this same sweep in its own onHide(), but this drawer never
  // called it at all, so the same failure here had no safety net and could stay stuck
  // indefinitely instead of clearing within 800ms. Bound to the drawer's own (onHide) rather
  // than toggleChat() below, since the drawer's built-in close affordances (backdrop click,
  // Escape) set chatOpen=false directly without going through toggleChat().
  onChatDrawerHide(): void {
    scheduleOrphanedOverlayMaskSweep();
  }

  toggleChat(): void {
    this.chatOpen = !this.chatOpen;
    this.fabPulse = false;
    if (this.chatOpen) this.openDrawer();
  }

  /** The open-side-effects previously inlined in toggleChat() above — split out so
   *  AiChatWidgetSharedService's openWithQuestion subscription (see constructor) can also open
   *  the drawer (e.g. from Quick Search's "Ask Linc" card) without duplicating this list. */
  private openDrawer(): void {
    // Init AudioContext during this user gesture so it's allowed to play later
    this.initAudioContext();
    this.playSound('open');
    this.scrollToBottomNeeded = true;
    this.refreshStatus();
    // Re-check both "Linc Data" gate layers on every open, not just once at page load — a tenant
    // ADMIN granting DATA_ASSISTANT_ACCESS (or ROOT granting the Forest addon) must be visible
    // the next time this widget is opened, without the user having to navigate anywhere first.
    this.refreshDataAssistantGate();
    this.refreshAutoStatus();
    this.permissionService.refreshPermissions().subscribe({
      next: () => this.enforceDataModeAccess(),
      error: () => {},
    });
    this.refreshDisclaimerStatus();
    this.enforceDataModeAccess();
    if (this.mode === 'data' && !this.dataSessionId) this.loadDataSessions();
  }

  sendMessage(): void {
    const text = this.inputText.trim();
    const isLoading = this.isBusy;
    if (!text || isLoading) return;
    this.inputText = '';
    this.resetTextareaHeight();
    // Defensive, on top of the ngModel binding above — found live 2026-08-20 that the typed
    // text can be left sitting in the box after send under some input paths (root cause not
    // fully pinned down; this guarantees the box is empty regardless of why).
    if (this.inputArea?.nativeElement) {
      this.inputArea.nativeElement.value = '';
    }
    if (this.mode === 'auto') this._doSendAuto(text);
    else if (this.mode === 'data') this._doSendData(text);
    else this._doSend(text);
  }

  sendStarter(text: string): void {
    if (this.mode === 'auto') this._doSendAuto(text);
    else if (this.mode === 'data') this._doSendData(text);
    else this._doSend(text);
  }

  /** A clicked disambiguation chip is sent exactly like a typed reply — same resend mechanism
   *  starter chips already use, just triggered by a candidate tap instead. Resolves through the
   *  EXACT SAME backend pendingDisambiguationCache flow a typed answer would.
   *  `candidate.value` (never shown) is still what's actually sent/resolved against; `displayText`
   *  (falling back to `name`) is what the user's own chat bubble shows — see displayText's own
   *  note on why the two can differ (a raw backend intent code vs. its friendly confirmation). */
  selectCandidate(candidate: DataAssistantCandidate): void {
    const shown = candidate.displayText ?? candidate.name;
    // A tapped choice answers a question Linc Data just asked, so it goes straight back to Data.
    if (this.mode === 'auto') this._doSendAuto(candidate.value, shown, 'data');
    else this._doSendData(candidate.value, shown);
  }

  /** Tapping a "you might also ask" suggestion — added 2026-08-23. Same "send it exactly like a
   *  typed question" mechanism as selectCandidate() above, kept as its own method (not reused)
   *  since the two mean different things: a candidate resolves THIS turn's pending question,
   *  a related question starts a brand-new one. */
  askRelatedQuestion(question: string): void {
    if (this.mode === 'auto') this._doSendAuto(question, question, 'data');
    else this._doSendData(question);
  }

  /** Linc HELP (KB mode)'s equivalent of askRelatedQuestion() above — same "send it exactly like a
   *  typed question" mechanism, just routed through _doSend() (KB backend) instead of
   *  _doSendData() (Data Assistant backend). */
  askKbRelatedQuestion(question: string): void {
    if (this.mode === 'auto') this._doSendAuto(question, question, 'help');
    else this._doSend(question);
  }

  /** Closes the drawer and navigates to a "View Details" deepLink. */
  goToDeepLink(deepLink: string): void {
    this.chatOpen = false;
    this.router.navigateByUrl(deepLink);
  }

  private _doSend(text: string): void {
    // loading check moved here (was only in sendMessage(), never here) — sendStarter() calls
    // this directly with no guard of its own, so a double-tap on a welcome-screen chip could
    // fire two sends before the first response returned, producing a duplicate message.
    if (this.loading || this.status.blocked || this.dailyLimitExhausted) return;

    const userMsg: ChatMessage & { sources?: string[] } = {
      id: 0,
      sessionId: this.currentSessionId ?? 0,
      role: 'user',
      content: text,
      attachmentName: this.pendingAttachment?.name,
      attachmentType: this.pendingAttachment?.type,
      createdAt: new Date().toISOString()
    };
    this.messages.push(userMsg);
    this.msgSources.push([]);
    this.scrollToBottomNeeded = true;
    this.loading = true;
    this.loadingStartedAt = Date.now();
    this.playSound('send');

    const req = {
      sessionId: this.currentSessionId,
      message: text,
      attachmentBase64: this.pendingAttachment?.base64 ?? null,
      attachmentName: this.pendingAttachment?.name ?? null,
      attachmentType: this.pendingAttachment?.type ?? null,
      platform: Capacitor.getPlatform(),
      appVersion: APP_VERSION,
    };
    this.clearAttachment();

    this.aiChatService.chat(req).subscribe({
      next: (res) => {
        // Real, reported symptom (2026-08-20): "the empty box gets wiped off immediately as
        // response is very quick" — a fast reply could flip loading back to false, and the
        // typing bubble away, within well under 100ms of appearing. At that speed it reads as
        // a blank/broken flash, not a recognizable "thinking" state. finishAfterMinLoading()
        // holds the typing indicator up for a minimum, consistent duration so every reply —
        // fast or slow — has the same clean "thinking → answer" transition.
        this.finishAfterMinLoading(() => {
          this.currentSessionId = res.sessionId;
          // Never push a message with no visible content — an empty string here would still
          // render a fully shaped, empty message bubble (the bubble paints its shape regardless
          // of content), which is exactly the "blank rectangle" artifact this guard prevents.
          const content = (res.message ?? '').trim();
          if (content) {
            const botMsg: ChatMessage & { sources?: string[] } = {
              id: res.messageId,
              sessionId: res.sessionId,
              role: 'assistant',
              content,
              createdAt: new Date().toISOString(),
              creditsUsed: res.creditsUsedThisCall ?? null,
              relatedQuestions: res.relatedQuestions ?? [],
              deepLink: res.deepLink ?? null,
            };
            this.messages.push(botMsg);
            this.msgSources.push(res.sources ?? []);
          }
          this.loading = false;
          this.scrollToBottomNeeded = true;
          this.playSound('receive');
          this.status = { ...this.status, dailyCount: this.status.dailyCount + 1 };
          this.loadSessions();
          this.cdr.detectChanges();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.finishAfterMinLoading(() => {
          this.loading = false;
          // Backstop only — the blocking gate screen should already prevent this from ever firing
          // in normal use. Re-shows the gate instead of a confusing raw error bubble.
          if (this.isDisclaimerError(err)) { this.disclaimerAccepted = false; this.cdr.detectChanges(); return; }
          let errorContent = 'Sorry, something went wrong. Please try again.';

          if (err.status === 429) {
            errorContent = err.error?.message ?? err.error ??
              `You have reached your daily AI Chat limit of ${this.status.dailyLimit} messages. Your limit resets at midnight.`;
            this.status = { ...this.status, dailyCount: this.status.dailyLimit };
          } else if (err.status === 403) {
            const msg: string = err.error?.message ?? err.error ?? '';
            if (msg.toLowerCase().includes('blocked')) {
              this.status = { ...this.status, blocked: true, blockedMessage: msg };
              errorContent = msg;
            } else {
              errorContent = msg || 'AI Chat is not available for your account.';
            }
          }

          this.messages.push({
            id: 0,
            sessionId: this.currentSessionId ?? 0,
            role: 'assistant',
            content: errorContent,
            createdAt: new Date().toISOString()
          });
          this.msgSources.push([]);
          this.scrollToBottomNeeded = true;
          this.cdr.detectChanges();
        });
      }
    });
  }

  // ── "Linc Data" mode send flow — parallel to _doSend() above, same shape, different backend. ──

  /** `displayText` is what the user's own chat bubble shows — defaults to `text` (a typed message
   *  or a starter chip, both already fine to show as-is). `selectCandidate()` passes the two apart
   *  when a disambiguation click's real resend value is a raw backend code that must never be
   *  shown — see DataAssistantCandidate.displayText's own note. `text` (never `displayText`) is
   *  always what actually gets sent to the backend, below. */
  private _doSendData(text: string, displayText: string = text): void {
    if (this.dataLoading) return;
    if (!this.canUseDataMode) { this.enforceDataModeAccess(); return; }

    const userMsg: DataChatMessage = {
      id: ++this.dataMsgSeq, role: 'user', content: displayText, createdAt: new Date().toISOString(),
      deepLink: null, resolved: true, candidates: [], relatedQuestions: [],
      formatted: { kind: 'text', html: this.formatMessage(displayText) },
    };
    this.dataMessages.push(userMsg);
    this.scrollToBottomNeeded = true;
    this.dataLoading = true;
    this.loadingStartedAt = Date.now();
    this.playSound('send');

    this.aiDataAssistantService.ask({ sessionId: this.dataSessionId, message: text }).subscribe({
      next: (res) => {
        this.finishAfterMinLoading(() => {
          this.dataSessionId = res.sessionId;
          const content = (res.answer ?? '').trim();
          if (content) {
            this.dataMessages.push({
              id: ++this.dataMsgSeq, role: 'assistant', content, createdAt: new Date().toISOString(),
              deepLink: res.deepLink ?? null, resolved: res.resolved, candidates: res.candidates ?? [],
              queryLogId: res.queryLogId ?? null, creditsUsed: res.creditsUsed ?? null,
              data: res.data ?? null, intent: res.intent ?? null,
              relatedQuestions: res.relatedQuestions ?? [],
              formatted: this.getFormattedAnswer(content, res.data),
            });
          }
          this.dataLoading = false;
          this.scrollToBottomNeeded = true;
          this.playSound('receive');
          this.loadDataSessions();
          this.cdr.detectChanges();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.finishAfterMinLoading(() => {
          this.dataLoading = false;
          if (this.isDisclaimerError(err)) { this.disclaimerAccepted = false; this.cdr.detectChanges(); return; }
          let errorContent = 'Sorry, something went wrong. Please try again.';
          if (err.status === 403) {
            errorContent = err.error?.message ?? 'Linc Data isn\'t available for your account.';
          } else if (err.status === 402) {
            errorContent = err.error?.message ?? 'Out of AI credits for this billing period.';
          }
          this.dataMessages.push({
            id: ++this.dataMsgSeq, role: 'assistant', content: errorContent, createdAt: new Date().toISOString(),
            deepLink: null, resolved: false, candidates: [], relatedQuestions: [],
            formatted: { kind: 'text', html: this.formatMessage(errorContent) },
          });
          this.scrollToBottomNeeded = true;
          this.cdr.detectChanges();
        });
      }
    });
  }

  // ── Auto mode send flow — one question box; the server decides Help or Data (or both). ──────

  /**
   * @param displayText what the person's own bubble shows (differs from `text` only for a tapped
   *                    "did you mean" choice whose real value is an internal code)
   * @param force       'help' | 'data' when this message answers a question that engine just asked
   */
  private _doSendAuto(text: string, displayText: string = text, force: 'help' | 'data' | null = null): void {
    if (this.autoLoading) return;
    if (!this.autoAvailable) { this.enforceAutoAccess(); return; }

    const attachment = this.pendingAttachment;
    const userMsg: ChatMessage & { sources?: string[] } = {
      id: 0,
      sessionId: this.currentSessionId ?? 0,
      role: 'user',
      content: displayText,
      attachmentName: attachment?.name,
      attachmentType: attachment?.type,
      createdAt: new Date().toISOString()
    };
    this.autoThread.push({ key: ++this.autoSeq, engine: 'kb', kb: userMsg, sources: [] });
    this.scrollToBottomNeeded = true;
    this.autoLoading = true;
    this.loadingStartedAt = Date.now();
    this.playSound('send');

    // A reply to a question Linc Data just asked goes back to Data even if the person typed it.
    const forceEngine = force ?? this.pendingForce;
    this.pendingForce = null;

    const req: AutoRequest = {
      message: text,
      helpSessionId: this.currentSessionId,
      dataSessionId: this.dataSessionId,
      lastEngine: this.lastEngine,
      forceEngine,
      attachmentBase64: attachment?.base64 ?? null,
      attachmentName: attachment?.name ?? null,
      attachmentType: attachment?.type ?? null,
      platform: Capacitor.getPlatform(),
      appVersion: APP_VERSION,
    };
    this.clearAttachment();

    this.aiChatService.auto(req).subscribe({
      next: (res) => {
        this.finishAfterMinLoading(() => {
          this.applyAutoResponse(res, displayText);
          this.autoLoading = false;
          this.scrollToBottomNeeded = true;
          this.playSound('receive');
          this.cdr.detectChanges();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.finishAfterMinLoading(() => {
          this.autoLoading = false;
          if (this.isDisclaimerError(err)) { this.disclaimerAccepted = false; this.cdr.detectChanges(); return; }
          this.pushAutoError(err);
          this.scrollToBottomNeeded = true;
          this.cdr.detectChanges();
        });
      }
    });
  }

  /** Puts the server's answer(s) into the merged Auto thread AND into the matching Help / Data thread. */
  private applyAutoResponse(res: AutoResponse, displayText: string): void {
    if (res.help) this.applyAutoHelp(res.help, displayText, res.note ?? null);
    if (res.data) this.applyAutoData(res.data, displayText);
    // A follow-up such as "and last month?" belongs with the engine that answered last; for a mixed
    // question that is Data, because the how-to half rarely has a follow-up.
    this.lastEngine = res.data ? 'data' : 'help';
  }

  private applyAutoHelp(h: ChatResponse, displayText: string, note: string | null): void {
    this.currentSessionId = h.sessionId;
    const now = new Date().toISOString();
    this.messages.push({ id: 0, sessionId: h.sessionId, role: 'user', content: displayText, createdAt: now });
    this.msgSources.push([]);
    const content = (h.message ?? '').trim();
    if (content) {
      const bot: ChatMessage & { sources?: string[] } = {
        id: h.messageId,
        sessionId: h.sessionId,
        role: 'assistant',
        content,
        createdAt: now,
        creditsUsed: h.creditsUsedThisCall ?? null,
        relatedQuestions: h.relatedQuestions ?? [],
        deepLink: h.deepLink ?? null,
      };
      this.messages.push(bot);
      this.msgSources.push(h.sources ?? []);
      this.autoThread.push({ key: ++this.autoSeq, engine: 'kb', kb: bot, sources: h.sources ?? [], note });
    }
    this.status = { ...this.status, dailyCount: this.status.dailyCount + 1 };
    this.loadSessions();
  }

  private applyAutoData(d: DataAssistantAskResponse, displayText: string): void {
    this.dataSessionId = d.sessionId;
    const now = new Date().toISOString();
    this.dataMessages.push({
      id: ++this.dataMsgSeq, role: 'user', content: displayText, createdAt: now,
      deepLink: null, resolved: true, candidates: [], relatedQuestions: [],
      formatted: { kind: 'text', html: this.formatMessage(displayText) },
    });
    const content = (d.answer ?? '').trim();
    if (content) {
      const bot: DataChatMessage = {
        id: ++this.dataMsgSeq, role: 'assistant', content, createdAt: now,
        deepLink: d.deepLink ?? null, resolved: d.resolved, candidates: d.candidates ?? [],
        queryLogId: d.queryLogId ?? null, creditsUsed: d.creditsUsed ?? null,
        data: d.data ?? null, intent: d.intent ?? null,
        relatedQuestions: d.relatedQuestions ?? [],
        formatted: this.getFormattedAnswer(content, d.data),
      };
      this.dataMessages.push(bot);
      this.autoThread.push({ key: ++this.autoSeq, engine: 'data', data: bot, sources: [] });
    }
    // Linc Data asked "did you mean ...?": the next message answers it, so send it back to Data.
    this.pendingForce = (d.candidates && d.candidates.length > 0) ? 'data' : null;
    this.loadDataSessions();
  }

  private pushAutoError(err: HttpErrorResponse): void {
    // 404 = the server switched Auto off while this screen was open: go back to Help quietly.
    if (err.status === 404) {
      this.autoAvailable = false;
      this.enforceAutoAccess();
      return;
    }
    let errorContent = 'Sorry, something went wrong. Please try again.';
    if (err.status === 429) {
      errorContent = err.error?.message ?? err.error ??
        `You have reached your daily AI Chat limit of ${this.status.dailyLimit} messages. Your limit resets at midnight.`;
      this.status = { ...this.status, dailyCount: this.status.dailyLimit };
    } else if (err.status === 402) {
      errorContent = err.error?.message ?? 'Out of AI credits for this billing period.';
    } else if (err.status === 403) {
      const msg: string = err.error?.message ?? err.error ?? '';
      if (typeof msg === 'string' && msg.toLowerCase().includes('blocked')) {
        this.status = { ...this.status, blocked: true, blockedMessage: msg };
      }
      errorContent = (typeof msg === 'string' && msg) ? msg : 'AI Chat is not available for your account.';
    }
    this.autoThread.push({
      key: ++this.autoSeq, engine: 'kb', sources: [],
      kb: { id: 0, sessionId: this.currentSessionId ?? 0, role: 'assistant', content: errorContent, createdAt: new Date().toISOString() },
    });
  }

  trackByAutoItem(_index: number, item: AutoThreadItem): number {
    return item.key;
  }

  /** New chat in Auto mode starts fresh in BOTH engines, so nothing from before leaks into the next question. */
  newAutoSession(): void {
    this.currentSessionId = null;
    this.messages = [];
    this.msgSources = [];
    this.feedbackMap = {};
    this.newDataSession();
    this.autoThread = [];
    this.lastEngine = null;
    this.pendingForce = null;
    this.historyOpen = false;
    this.clearAttachment();
  }

  newDataSession(): void {
    this.dataSessionId = null;
    this.dataMessages = [];
    this.dataFeedbackMap = {};
    this.historyOpen = false;
  }

  loadDataSessions(): void {
    if (!this.canUseDataMode) return; // hidden = no access (2026-09-16)
    this.aiDataAssistantService.getSessions().subscribe({
      next: (sessions) => { this.dataSessions = sessions; },
      error: () => {},
    });
  }

  loadDataSession(sessionId: string): void {
    this.dataSessionId = sessionId;
    this.historyOpen = false;
    this.dataLoading = true;
    this.aiDataAssistantService.getMessages(sessionId).subscribe({
      next: (msgs: DataAssistantMessage[]) => {
        this.dataMessages = msgs.map((m) => ({
          id: ++this.dataMsgSeq, role: m.role, content: m.content, createdAt: m.createdAt,
          deepLink: m.deepLink, resolved: m.resolved, candidates: [], relatedQuestions: [],
          // History never carries the structured `data` payload (not persisted server-side —
          // see AiDataAssistantQueryLog), so a reloaded past answer always renders as plain text,
          // same as it always has — only a message from THIS live session can be richly formatted.
          formatted: { kind: 'text' as const, html: this.formatMessage(m.content) },
        }));
        this.dataLoading = false;
        this.scrollToBottomNeeded = true;
        this.cdr.detectChanges();
      },
      error: () => { this.dataLoading = false; },
    });
  }

  deleteDataSession(sessionId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.aiDataAssistantService.deleteSession(sessionId).subscribe({
      next: () => {
        this.dataSessions = this.dataSessions.filter(s => s.sessionId !== sessionId);
        if (this.dataSessionId === sessionId) this.newDataSession();
      }
    });
  }

  sendDataTranscript(): void {
    if (!this.dataSessionId) return;
    this.showEmailDialog = false;
    this.aiDataAssistantService.emailTranscript(this.dataSessionId, this.transcriptEmail || undefined).subscribe();
  }

  /** See the call sites' own note (in the chat() subscribe above) for why this exists. */
  private finishAfterMinLoading(work: () => void): void {
    const elapsed = Date.now() - this.loadingStartedAt;
    const remaining = AiChatWidgetComponent.MIN_LOADING_MS - elapsed;
    if (remaining > 0) {
      setTimeout(work, remaining);
    } else {
      work();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    // isComposing: true while an IME candidate list is open (e.g. confirming a Chinese/
    // Japanese/Korean or predictive-text candidate) — Enter in that state should confirm the
    // candidate, not send the message. keyCode 229 is the legacy fallback some older mobile
    // WebViews still report instead of a real key value during composition.
    if (event.isComposing || event.keyCode === 229) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /** Shared by both the Linc/KB and "Linc Data" message loops — loosened to `{id: number}` (not
   *  ChatMessage) so it structurally accepts DataChatMessage too. */
  trackByMsgId(index: number, msg: { id: number }): number {
    return msg.id || index;
  }

  autoResize(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 128) + 'px';
  }

  private resetTextareaHeight(): void {
    if (this.inputArea?.nativeElement) {
      this.inputArea.nativeElement.style.height = '';
    }
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.handleAttachmentFile(file);
  }

  /** Also called directly from the media-capture-button (Take Photo/Choose Gallery/Browse). */
  onMediaCaptured(files: File[]): void {
    const file = files[0];
    if (file) this.handleAttachmentFile(file);
  }

  private handleAttachmentFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const base64 = dataUrl.split(',')[1];
      this.pendingAttachment = { name: file.name, type: file.type, base64 };
    };
    reader.readAsDataURL(file);
  }

  clearAttachment(): void {
    this.pendingAttachment = null;
  }

  isImage(type: string | undefined): boolean {
    return !!type && type.startsWith('image/');
  }

  insertEmoji(emoji: string, panel: Popover): void {
    this.inputText += emoji;
    panel.hide();
  }

  newSession(): void {
    if (this.mode === 'auto') { this.newAutoSession(); return; }
    if (this.mode === 'data') { this.newDataSession(); return; }
    this.currentSessionId = null;
    this.messages = [];
    this.msgSources = [];
    this.feedbackMap = {};
    this.historyOpen = false;
    this.clearAttachment();
  }

  /** Was a plain inline `historyOpen = !historyOpen` binding in the template — opening never
   *  actually fetched anything itself, it only relied on refreshStatus() having already loaded
   *  sessions once on init. Fetching here too (only when the current mode's list is still empty)
   *  makes the button self-contained: it can't go dead again from some other future timing gap,
   *  and it stays cheap since a re-open with data already loaded doesn't refetch. */
  toggleHistory(): void {
    this.historyOpen = !this.historyOpen;
    if (!this.historyOpen) return;
    if (this.mode === 'data') {
      if (this.dataSessions.length === 0) this.loadDataSessions();
    } else if (this.sessions.length === 0) {
      this.loadSessions();
    }
  }

  loadSessions(): void {
    this.aiChatService.getSessions().subscribe({
      next: (sessions) => { this.sessions = sessions; },
      error: () => {}
    });
  }

  loadSession(sessionId: number): void {
    this.currentSessionId = sessionId;
    this.historyOpen = false;
    this.loading = true;
    this.aiChatService.getMessages(sessionId).subscribe({
      next: (msgs) => {
        this.messages = msgs;
        this.msgSources = msgs.map(() => []);
        this.loading = false;
        this.scrollToBottomNeeded = true;
        this.cdr.detectChanges();
      },
      error: () => { this.loading = false; }
    });
  }

  deleteSession(sessionId: number, event: MouseEvent): void {
    event.stopPropagation();
    this.aiChatService.deleteSession(sessionId).subscribe({
      next: () => {
        this.sessions = this.sessions.filter(s => s.id !== sessionId);
        if (this.currentSessionId === sessionId) {
          this.newSession();
        }
      }
    });
  }

  submitFeedback(messageId: number, rating: 1 | -1): void {
    const previous = this.feedbackMap[messageId];
    const previousActivePrompt = this.activeReasonPromptId;
    this.feedbackMap[messageId] = rating; // optimistic
    // Added 2026-08-23 — a thumbs-down opens the "why" reason chip row for this message (closing
    // any other message's open row); a thumbs-up closes it and clears any locally-shown reason,
    // matching the backend's own "rating >= 0 clears reason" rule (see submitFeedback's own
    // server-side note) — purely a display-state mirror, the server is the actual source of truth.
    this.activeReasonPromptId = rating === -1 ? messageId : null;
    if (rating === 1) {
      delete this.reasonMap[messageId];
    }
    this.aiChatService.submitFeedback(messageId, rating).subscribe({
      next: () => { /* already reflected optimistically, nothing more to do */ },
      error: () => {
        // Roll back — a prior version of this call had no error handler at all, so a failed
        // save looked identical to a successful one (thumb stayed "active" either way).
        if (previous === undefined) {
          delete this.feedbackMap[messageId];
        } else {
          this.feedbackMap[messageId] = previous;
        }
        this.activeReasonPromptId = previousActivePrompt;
        this.cdr.detectChanges();
      }
    });
  }

  /** Added 2026-08-23 — a reason-chip pick, always a SEPARATE call after the initial thumbs-down
   *  (never blocks/gates it) — re-sends the already-active rating (-1, the only rating this ever
   *  fires from) alongside the chosen reason. Same optimistic-then-rollback shape as
   *  submitFeedback() above. */
  selectFeedbackReason(messageId: number, reason: string): void {
    const previous = this.reasonMap[messageId];
    this.reasonMap[messageId] = reason; // optimistic
    this.aiChatService.submitFeedback(messageId, -1, undefined, reason).subscribe({
      next: () => { this.activeReasonPromptId = null; this.cdr.detectChanges(); },
      error: () => {
        if (previous === undefined) {
          delete this.reasonMap[messageId];
        } else {
          this.reasonMap[messageId] = previous;
        }
        this.cdr.detectChanges();
      }
    });
  }

  /** Feedback credit reward — added 2026-08-22. Shown only when the backend actually granted one
   *  (real double-grant protection lives server-side — see AiDataAssistantController's own note —
   *  this is purely the "tell the user it happened" visual). Cleared automatically after a few
   *  seconds; a second reward while one is still showing just restarts the timer with the new
   *  amount rather than stacking. */
  rewardBumpAmount: number | null = null;
  private rewardBumpTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Toggled by tapping the credits-used text in the usage row — see the template's own note. */
  showCreditsBreakdown = false;

  /** Added 2026-08-22 — Linc Data's own feedback submit, separate from submitFeedback (KB) above:
   *  different backend endpoint, different id space, same optimistic-then-rollback shape. */
  submitDataFeedback(queryLogId: number, rating: 1 | -1): void {
    const previous = this.dataFeedbackMap[queryLogId];
    const previousActivePrompt = this.activeDataReasonPromptId;
    this.dataFeedbackMap[queryLogId] = rating; // optimistic
    // Added 2026-08-23 — same "why" reason chip row open/close as submitFeedback() (KB) above.
    this.activeDataReasonPromptId = rating === -1 ? queryLogId : null;
    if (rating === 1) {
      delete this.dataReasonMap[queryLogId];
    }
    this.aiDataAssistantService.submitFeedback(queryLogId, rating).subscribe({
      next: (res) => {
        if (res?.creditsRewarded > 0) {
          if (this.rewardBumpTimeout) clearTimeout(this.rewardBumpTimeout);
          this.rewardBumpAmount = res.creditsRewarded;
          // Real total refreshed from the server (not computed locally) so the number shown
          // afterward is never at risk of drifting from what the backend actually clamped/granted.
          this.refreshAiCreditsUsage();
          this.rewardBumpTimeout = setTimeout(() => {
            this.rewardBumpAmount = null;
            this.cdr.detectChanges();
          }, 2500);
          // Count THIS answer's own "X credits used" badge down too, e.g. "72" -> "62" — the
          // visual the user actually asked for, distinct from the header bump above.
          const rewardedMsg = this.dataMessages.find(m => m.queryLogId === queryLogId);
          if (rewardedMsg && rewardedMsg.creditsUsed != null) {
            this.animateCreditsCountdown(rewardedMsg, Math.max(0, rewardedMsg.creditsUsed - res.creditsRewarded), res.creditsRewarded);
          }
          this.cdr.detectChanges();
        }
      },
      error: () => {
        if (previous === undefined) {
          delete this.dataFeedbackMap[queryLogId];
        } else {
          this.dataFeedbackMap[queryLogId] = previous;
        }
        this.activeDataReasonPromptId = previousActivePrompt;
        this.cdr.detectChanges();
      }
    });
  }

  /** Added 2026-08-23 — Linc Data's own reason-chip pick, separate from selectFeedbackReason (KB)
   *  above: different backend endpoint, different id space, same optimistic-then-rollback shape.
   *  Never re-triggers the credit-reward flow — that only ever fires on the FIRST feedback call
   *  per query (the create-new branch server-side), and this always re-sends rating -1 against an
   *  already-existing feedback row, hitting the update branch instead. */
  selectDataFeedbackReason(queryLogId: number, reason: string): void {
    const previous = this.dataReasonMap[queryLogId];
    this.dataReasonMap[queryLogId] = reason; // optimistic
    this.aiDataAssistantService.submitFeedback(queryLogId, -1, reason).subscribe({
      next: () => { this.activeDataReasonPromptId = null; this.cdr.detectChanges(); },
      error: () => {
        if (previous === undefined) {
          delete this.dataReasonMap[queryLogId];
        } else {
          this.dataReasonMap[queryLogId] = previous;
        }
        this.cdr.detectChanges();
      }
    });
  }

  /** Added 2026-08-22 — counts one message's own "X credits used" badge down to its real
   *  post-reward value (e.g. "72 credits used" -> "62 credits used"), instead of just jumping
   *  straight to the new number. Fixed 700ms total duration regardless of reward size (10 or
   *  500 credits both finish at the same pace) — deliberately simple, no easing, just a linear
   *  countdown that always lands exactly on the real target value. Keep small — see
   *  feedback-animation-ui-polish-caution memory on past animation work being unsatisfying. */
  private animateCreditsCountdown(msg: DataChatMessage, targetValue: number, rewardAmount: number): void {
    const startValue = msg.creditsUsed ?? targetValue;
    if (startValue <= targetValue) {
      msg.creditsUsed = targetValue;
      this.cdr.detectChanges();
      return;
    }
    // creditsAnimating drives a brief scale/glow pulse in the template — added 2026-08-23. The
    // countdown itself already worked, it was just easy to miss at this font size; this doesn't
    // change the countdown's timing/values at all, only flags the SAME 700ms window so the
    // number visually stands out while it's actually changing. Cleared a beat after the count
    // finishes so the pulse has time to settle rather than cutting off right on the final digit.
    msg.creditsAnimating = true;
    // creditsRewardBadgeAmount drives the floating "+N credits back" badge, added 2026-08-23 —
    // same lifetime as creditsAnimating (set here, cleared in the same cleanup below) so both
    // effects start and end together without a second timer.
    msg.creditsRewardBadgeAmount = rewardAmount;
    const durationMs = 700;
    const startTime = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, (now - startTime) / durationMs);
      msg.creditsUsed = Math.round(startValue - (startValue - targetValue) * progress);
      this.cdr.detectChanges();
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        msg.creditsUsed = targetValue;
        setTimeout(() => {
          msg.creditsAnimating = false;
          msg.creditsRewardBadgeAmount = undefined;
          this.cdr.detectChanges();
        }, 300);
        this.cdr.detectChanges();
      }
    };
    requestAnimationFrame(step);
  }

  promptEmailTranscript(): void {
    this.transcriptEmail = '';
    this.showEmailDialog = true;
  }

  sendTranscript(): void {
    if (this.mode === 'data') { this.sendDataTranscript(); return; }
    if (!this.currentSessionId) return;
    this.showEmailDialog = false;
    this.aiChatService.emailTranscript(this.currentSessionId, this.transcriptEmail || undefined)
      .subscribe();
  }

  toggleSound(): void {
    this.soundEnabled = !this.soundEnabled;
    localStorage.setItem('ai-chat-sound', String(this.soundEnabled));
    if (this.soundEnabled) this.playSound('open');
  }

  // ── Resizable drawer width — added 2026-08-23. Drag the handle on the panel's right edge
  // (the panel itself is pinned to the LEFT of the screen via position="left" on p-drawer, so
  // dragging right widens it). Bound as arrow-function properties so `this` stays correct and
  // the exact same function reference can be removed again in onResizeEnd(). ──
  onResizeStart(event: MouseEvent | TouchEvent): void {
    this.isResizingDrawer = true;
    this.resizeStartX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    this.resizeStartWidth = this.drawerWidthPx;
    this.resizePanelEl = document.querySelector('.ai-chat-sidebar');
    event.preventDefault();
    window.addEventListener('mousemove', this.onResizeMove);
    window.addEventListener('mouseup', this.onResizeEnd);
    window.addEventListener('touchmove', this.onResizeMove, { passive: false });
    window.addEventListener('touchend', this.onResizeEnd);
  }

  private onResizeMove = (event: MouseEvent | TouchEvent): void => {
    if (!this.isResizingDrawer) return;
    event.preventDefault();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const delta = clientX - this.resizeStartX;
    const maxAllowed = Math.min(AiChatWidgetComponent.DRAWER_MAX_WIDTH, window.innerWidth - 40);
    this.drawerWidthPx = Math.max(AiChatWidgetComponent.DRAWER_MIN_WIDTH, Math.min(maxAllowed, this.resizeStartWidth + delta));
    // Write straight to the real panel DOM node too — p-drawer's own [style] input (bound in the
    // template) doesn't visibly repaint until some unrelated later event forces it, since the
    // panel renders via appendTo="body" outside this component's DOM subtree. This direct write
    // is synchronous and guarantees the drag feels live; drawerWidthPx below stays the single
    // source of truth for persistence/next-open sizing, unchanged.
    if (this.resizePanelEl) {
      this.resizePanelEl.style.width = this.drawerWidthPx + 'px';
    }
    this.cdr.detectChanges();
  };

  private onResizeEnd = (): void => {
    this.isResizingDrawer = false;
    this.resizePanelEl = null;
    window.removeEventListener('mousemove', this.onResizeMove);
    window.removeEventListener('mouseup', this.onResizeEnd);
    window.removeEventListener('touchmove', this.onResizeMove);
    window.removeEventListener('touchend', this.onResizeEnd);
    localStorage.setItem(AiChatWidgetComponent.DRAWER_WIDTH_KEY, String(this.drawerWidthPx));
  };

  formatMessage(content: string): SafeHtml {
    const escaped = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>');
    return this.sanitizer.bypassSecurityTrustHtml(escaped);
  }

  // ── "Linc Data" rich-answer formatting — added 2026-08-23 ────────────────
  // ONE method (getFormattedAnswer) governs every formatting decision for an assistant answer.
  // It never adds, removes, or rewords anything — it only decides HOW the exact same content/
  // data already returned gets laid out. The decision is driven by the actual SHAPE of `data`,
  // not a per-intent switch, so it stays correct as new question types are added later without
  // needing a matching frontend change every time. Always falls back to the existing plain
  // formatMessage() rendering — today's exact behavior — whenever the shape is ambiguous, empty,
  // or doesn't cleanly fit a richer presentation.

  /** Friendly column/stat labels for known backend field names (see AiDataAssistantService.java's
   *  various `data.put(...)` calls) — anything not listed here falls back to a humanized version
   *  of the raw camelCase key, never the raw key itself. */
  private static readonly FRIENDLY_LABELS: Record<string, string> = {
    companyName: 'Company', contactPerson: 'Contact', invoiced: 'Invoiced', paid: 'Paid',
    balance: 'Balance', amount: 'Amount', totalInvoiceAmount: 'Amount', currency: 'Currency',
    shipmentDate: 'Date', totalCbm: 'Total CBM', totalCft: 'Total CFT', totalPieces: 'Total Pieces',
    sheetCount: 'Sheets', count: 'Count', receivableDue: 'Receivable Due', payableDue: 'Payable Due',
    received: 'Received', totalShipments: 'Shipments', shipmentCount: 'Shipments', runCount: 'Runs',
    lotCode: 'Lot', loadingSiteName: 'Site', currentCbm: 'CBM', value: 'Value (est.)',
    lastMovement: 'Last Moved', machineName: 'Machine', operatorName: 'Operator',
    materialFrom: 'Material From', runCode: 'Run', isActive: 'Active', createdBy: 'Created By',
    createdDate: 'Created', lastModifiedBy: 'Updated By', lastModifiedDate: 'Last Updated',
    exchangeRateToBase: 'Exchange Rate', taxId: 'Tax ID', reportingCurrency: 'Currency',
    phone1: 'Phone', email: 'Email', web: 'Website', species: 'Species', grade: 'Grade',
    site: 'Site', status: 'Status', totalPayable: 'Payable',
  };

  private getFormattedAnswer(content: string, data?: Record<string, unknown> | null): FormattedAnswer {
    // Plain text for every live-data answer (2026-10-09): the tiles, tables and ranked cards looked odd and
    // were cut off in the narrow chat window. The server now writes each answer as one opening line, one
    // "- " line per item and one short note; a leading "- " is shown as a bullet. The old table / ranked /
    // stat builders below stay in the file but are no longer used.
    const fallback = (): FormattedAnswer => ({ kind: 'text', html: this.formatMessage((content ?? '').replace(/^- /gm, '• ')) });
    return fallback();
  }

  /** Columns auto-derived from the first row's own keys — deliberately simple for v1: any key
   *  whose value is itself an array/object (e.g. CONTACT_RECENT_CONTAINERS_YIELD's nested `units`)
   *  is skipped rather than exploded into a sub-table. */
  private buildTable(rows: Record<string, unknown>[]): FormattedAnswer {
    const first = rows[0];
    const keys = Object.keys(first).filter(k => {
      const v = first[k];
      return v === null || v === undefined || ['string', 'number', 'boolean'].includes(typeof v);
    });
    const columns: FormattedTableColumn[] = keys.map(k => ({ key: k, label: this.humanizeKey(k) }));
    const outRows = rows.map(r => {
      const out: Record<string, string> = {};
      for (const k of keys) out[k] = this.formatScalarValue(k, r[k]);
      return out;
    });
    return { kind: 'table', columns, rows: outRows };
  }

  /** Field names and verdict thresholds copied EXACTLY from buildPartnerPerformanceEntry /
   *  describePartnerPerformance in tms-sb's AiDataAssistantService.java — so a badge here can
   *  never disagree with the sentence sitting right next to it. */
  private buildRankedItem(entry: Record<string, unknown>, rank: number): RankedItem {
    const onTime = entry['onTimePaymentRatePct'] as number | null | undefined;
    const loss = entry['reconciliationLossRatePct'] as number | null | undefined;
    const outturn = entry['avgResawOutturnRatio'] as number | null | undefined;
    return {
      rank,
      companyName: (entry['companyName'] as string) ?? 'Unknown',
      onTimeLabel: onTime == null ? null : `${Math.round(onTime)}% on time`,
      onTimeSeverity: onTime == null ? null : onTime >= 90 ? 'success' : onTime >= 70 ? 'info' : onTime >= 50 ? 'warn' : 'danger',
      lossLabel: loss == null ? null : `${loss.toFixed(1)}% loss`,
      lossSeverity: loss == null ? null : loss <= 1 ? 'success' : loss <= 3 ? 'info' : loss <= 6 ? 'warn' : 'danger',
      outturnLabel: outturn == null ? null : `${(outturn * 100).toFixed(1)}% outturn`,
      shipmentCount: (entry['shipmentCount'] as number) ?? 0,
    };
  }

  private humanizeKey(key: string): string {
    if (AiChatWidgetComponent.FRIENDLY_LABELS[key]) return AiChatWidgetComponent.FRIENDLY_LABELS[key];
    const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, s => s.toUpperCase()).trim();
    return spaced.endsWith(' Pct') ? spaced.slice(0, -4) + ' %' : spaced;
  }

  private formatScalarValue(key: string, value: unknown): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') {
      const formatted = Number.isInteger(value) ? value.toLocaleString() : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
      return key.endsWith('Pct') ? `${formatted}%` : formatted;
    }
    return String(value);
  }

  /** Message timestamp, iMessage-style — added 2026-08-22. `new Date(createdAt)` + the browser's
   *  own Intl formatting (toLocaleTimeString/toLocaleDateString, no explicit timeZone option) are
   *  what actually make this show in the VIEWER's own browser timezone, not the server's — this
   *  only works correctly because the backend now sends a real UTC instant (see
   *  LocalDateTimeAsInstantSerializer on AiChatMessage/DataAssistantMessageDto server-side; the
   *  old naive-LocalDateTime JSON had no zone marker, so the browser silently mis-read it as
   *  already-local instead of converting it). Today -> time only; yesterday -> "Yesterday, time";
   *  older this year -> "Aug 20, time"; older, different year -> "Aug 20, 2025, time". */
  formatMsgTime(createdAt: string): string {
    const d = new Date(createdAt);
    if (isNaN(d.getTime())) return '';
    // 2026-09-15: day comparison and text in the user's profile timezone (not the browser's).
    const now = new Date();
    const dayKey = (x: Date) => formatInProfileZone(x, 'yyyy-MM-dd');
    const dayDiff = Math.round((Date.parse(dayKey(now)) - Date.parse(dayKey(d))) / 86400000);
    const time = formatInProfileZone(d, 'h:mm a');
    if (dayDiff === 0) return time;
    if (dayDiff === 1) return `Yesterday, ${time}`;
    const sameYear = formatInProfileZone(d, 'yyyy') === formatInProfileZone(now, 'yyyy');
    const datePart = formatInProfileZone(d, sameYear ? 'MMM d' : 'MMM d, y');
    return `${datePart}, ${time}`;
  }

  private scrollToBottom(): void {
    try {
      const el = this.messagesContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {}
  }

  private initAudioContext(): void {
    if (!this.soundEnabled) return;
    try {
      if (!this.audioCtx) {
        this.audioCtx = new AudioContext();
      } else if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }
    } catch {}
  }

  private playSound(type: 'send' | 'receive' | 'open'): void {
    if (!this.soundEnabled) return;
    try {
      if (!this.audioCtx) this.audioCtx = new AudioContext();
      const ctx = this.audioCtx;
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => this._scheduleTone(ctx, type));
      } else {
        this._scheduleTone(ctx, type);
      }
    } catch {}
  }

  private _scheduleTone(ctx: AudioContext, type: 'send' | 'receive' | 'open'): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);

    switch (type) {
      case 'send':
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        osc.frequency.linearRampToValueAtTime(660, ctx.currentTime + 0.09);
        osc.start(); osc.stop(ctx.currentTime + 0.1);
        break;
      case 'receive':
        osc.frequency.setValueAtTime(550, ctx.currentTime);
        osc.frequency.linearRampToValueAtTime(880, ctx.currentTime + 0.14);
        osc.start(); osc.stop(ctx.currentTime + 0.16);
        break;
      case 'open':
        osc.frequency.setValueAtTime(600, ctx.currentTime);
        osc.frequency.linearRampToValueAtTime(750, ctx.currentTime + 0.08);
        osc.start(); osc.stop(ctx.currentTime + 0.1);
        break;
    }
  }
}
